import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Banner } from "@/components/ui/banner"
import { getAllowedAppointmentTypes, isFlatLength } from "@/lib/appointment-type-rules"
import { isMedgateConnect } from "@/modules/patients/agenda/teleconsultation/booking/constants/medgate-connect"
import { TeleconsultationMode } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-drawer"
import {
  PartnerUser,
  TeleconsultationForm,
} from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { ConsultationTypeGroup } from "@/modules/patients/agenda/teleconsultation/booking/urgency/appointment-type-group"
import { FlexibilityGroup } from "@/modules/patients/agenda/teleconsultation/booking/urgency/flexibility-group"
import { FollowUpSection } from "@/modules/patients/agenda/teleconsultation/booking/urgency/follow-up-section"
import { MedicalUrgencyGroup } from "@/modules/patients/agenda/teleconsultation/booking/urgency/medical-urgency-group"
import type { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { PatientData } from "@/modules/patients/detail/store/usePatientStore"
import { Service, Urgency } from "@/types/__generated__/union-types"

export const ADMIN_SERVICES: Service[] = ["STUDY_CALL", "ADMINISTRATIVE_REQUEST"]

interface UrgencySectionProps {
  mode: TeleconsultationMode
  countryPatient: NonNullable<PatientData>
  followUpProgressNotes: ProgressNoteAndParentData[]
  partners: PartnerUser[]
  onUrgencyChange?: (urgency: Urgency) => void
  isAutoSwitchedToKidsLine?: boolean
}

const UrgencySection = ({
  mode,
  countryPatient,
  followUpProgressNotes,
  partners,
  onUrgencyChange,
  isAutoSwitchedToKidsLine,
}: UrgencySectionProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<TeleconsultationForm>()

  const medicalUrgencyWatch = useWatch({ control, name: "urgency" })
  const serviceWatch = useWatch({ control, name: "service" })
  const appointmentTypeWatch = useWatch({ control, name: "appointmentType" })
  const isMedgateConnectService = isMedgateConnect(serviceWatch)
  const allowedAppointmentTypes = getAllowedAppointmentTypes(serviceWatch)
  const isFlatLengthAppointment = isFlatLength(appointmentTypeWatch)

  if (isMedgateConnectService && mode.kind === "FOLLOWUP_FOR_PROGRESS_NOTE") return null

  return (
    <section className="grid gap-4">
      {!isMedgateConnectService && !isFlatLengthAppointment && (
        <>
          <MedicalUrgencyGroup onUrgencyChange={onUrgencyChange} isAutoSwitchedToKidsLine={isAutoSwitchedToKidsLine} />
          {medicalUrgencyWatch === "NORMAL" && (
            <Controller
              control={control}
              name="flexibility"
              render={({ field: { value, onChange } }) => <FlexibilityGroup value={value} onChange={onChange} />}
            />
          )}
        </>
      )}
      {isFlatLengthAppointment && (
        <Banner type="inline" variant="info" data-testid="consultation-type-full-hour-restriction">
          {t("patient.agenda.fullHourRestriction.consultationType")}
        </Banner>
      )}
      {serviceWatch !== "ADMINISTRATIVE_REQUEST" &&
        allowedAppointmentTypes.length > 1 &&
        mode.kind !== "FOLLOWUP_FOR_PROGRESS_NOTE" && (
          <>
            <Controller
              control={control}
              name="appointmentType"
              render={({ field: { value, onChange } }) => (
                <ConsultationTypeGroup
                  value={value}
                  onChange={onChange}
                  selectableAppointmentTypes={allowedAppointmentTypes}
                  isFollowUpDisabled={followUpProgressNotes.length === 0}
                />
              )}
            />
            {appointmentTypeWatch === "FOLLOW_UP" && (
              <FollowUpSection
                mode={mode}
                followUpProgressNotes={followUpProgressNotes}
                countryPatient={countryPatient}
                partners={partners}
              />
            )}
          </>
        )}
    </section>
  )
}

export { UrgencySection }
